/**
 * Utilitaires Web Push (PWA) côté client.
 *
 * Permet à un citoyen d'opter pour les notifications push des alertes
 * municipales. Workflow :
 *   1. Vérifie support navigateur (serviceWorker + PushManager)
 *   2. Demande permission Notification
 *   3. Récupère VAPID public key depuis l'API
 *   4. Subscribe via PushManager
 *   5. Envoie l'endpoint au backend
 *
 * Pour unsubscribe : appelle PushManager.unsubscribe puis API DELETE.
 *
 * Note : le service worker doit être enregistré au démarrage de l'app
 * (généralement dans main.tsx ou App.tsx). Ce module n'enregistre pas
 * le SW, il assume qu'il est déjà actif sur navigator.serviceWorker.
 */

import { api } from '@/lib/api';

/** Vérifie le support navigateur. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Convertit une clé VAPID base64-url en Uint8Array pour applicationServerKey. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

/** Convertit ArrayBuffer en base64-url pour transit JSON. */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]!);
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Subscribe le navigateur courant aux notifications push.
 * Throws si l'utilisateur refuse, si le navigateur ne supporte pas, ou si l'API échoue.
 */
export async function subscribeToPushNotifications(): Promise<void> {
  if (!isPushSupported()) {
    throw new Error('Votre navigateur ne supporte pas les notifications push');
  }

  // R13.5 — Vérifier que le serveur supporte VAPID AVANT d'appeler
  // Notification.requestPermission() (qui afficherait la modale browser native).
  // Si VAPID pas configuré côté serveur (503), on ne déclenche PAS la prompt browser.
  let publicKey: string;
  try {
    const r = await api.grc.alertes.vapidPublicKey();
    publicKey = r.publicKey;
  } catch (err) {
    throw new Error(
      'Notifications push pas encore disponibles sur ce serveur (VAPID non configuré)'
    );
  }

  // 1. Permission (modale browser native — inévitable en web, mais maintenant on est sûr
  //    que le backend supporte le push avant de la déclencher)
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permission de notifications refusée');
  }

  // 2. Service Worker prêt
  const registration = await navigator.serviceWorker.ready;

  // 3. Subscribe via PushManager (publicKey déjà obtenue plus haut)
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
  });

  // 4. Envoie au backend
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Subscription incomplète (endpoint/keys manquants)');
  }
  await api.grc.alertes.subscribePush({
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  });
}

/**
 * R13.5 — Détecte si le serveur supporte le push (clés VAPID configurées).
 * Permet à l'UI de désactiver le toggle push avant que l'utilisateur ne clique
 * (donc avant que `Notification.requestPermission()` ne déclenche la modale browser).
 * Retourne true si VAPID OK, false sinon. Ne throw jamais.
 */
export async function isServerPushAvailable(): Promise<boolean> {
  try {
    await api.grc.alertes.vapidPublicKey();
    return true;
  } catch {
    return false;
  }
}

/** Unsubscribe le navigateur courant. */
export async function unsubscribeFromPushNotifications(): Promise<void> {
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) await subscription.unsubscribe();
  await api.grc.alertes.unsubscribePush();
}

/** Vérifie si l'utilisateur courant est subscribed (côté navigateur). */
export async function isCurrentlySubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}
